import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trophy, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/request-password-reset", { email });
      setSent(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <h1 className="font-display text-4xl lg:text-5xl mb-2 text-[#0B0B12]">FORGOT PASSWORD?</h1>
      <p className="text-[#4B5563] mb-10">
        {sent ? "Check your inbox — we sent you a reset link." : "Enter your email and we'll send you a reset link."}
      </p>

      {sent ? (
        <div className="space-y-6" data-testid="forgot-sent-panel">
          <div className="flex items-center gap-3 p-4 border border-[#34C759]/30 bg-[#34C759]/10 rounded-sm">
            <CheckCircle2 className="w-5 h-5 text-[#34C759] flex-shrink-0" />
            <div className="text-sm">
              If an account exists for <b>{email}</b>, a reset link is on its way. Link expires in 30 minutes.
            </div>
          </div>
          <Link to="/login" data-testid="forgot-back-to-login">
            <Button className="w-full bg-[#1E4FFF] hover:bg-[#1740D6] text-white font-label h-12 rounded-sm">
              Back to login
            </Button>
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-6">
          <div>
            <Label className="font-label text-[#4B5563]">Email</Label>
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="mt-2 bg-white border-[#D5D9E2] h-12 rounded-sm text-[#0B0B12] placeholder:text-[#9AA1AD] focus-visible:ring-[#1E4FFF]"
              data-testid="forgot-email-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#1E4FFF] hover:bg-[#1740D6] text-white font-label h-12 rounded-sm"
            data-testid="forgot-submit-button"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link →"}
          </Button>
          <p className="text-sm text-[#4B5563]">
            <Link to="/login" className="text-[#1E4FFF] hover:underline" data-testid="forgot-login-link">
              Back to login
            </Link>
          </p>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw !== pw2) {
      setErr("Passwords don't match.");
      return;
    }
    if (pw.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password reset — log in with your new one.");
      navigate("/login");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <h1 className="font-display text-4xl lg:text-5xl mb-2 text-[#0B0B12]">SET NEW PASSWORD.</h1>
      <p className="text-[#4B5563] mb-10">Pick something strong — 8+ characters.</p>

      {!token ? (
        <div className="text-sm text-[#E13C32] bg-[#E13C32]/10 border border-[#E13C32]/30 p-3 rounded-sm">
          Missing reset token. Request a new link from the <Link to="/forgot-password" className="underline">forgot password page</Link>.
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-6">
          <div>
            <Label className="font-label text-[#4B5563]">New password</Label>
            <Input
              type="password"
              required
              minLength={8}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="••••••••"
              className="mt-2 bg-white border-[#D5D9E2] h-12 rounded-sm text-[#0B0B12] placeholder:text-[#9AA1AD] focus-visible:ring-[#1E4FFF]"
              data-testid="reset-pw-input"
            />
          </div>
          <div>
            <Label className="font-label text-[#4B5563]">Confirm password</Label>
            <Input
              type="password"
              required
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              placeholder="••••••••"
              className="mt-2 bg-white border-[#D5D9E2] h-12 rounded-sm text-[#0B0B12] placeholder:text-[#9AA1AD] focus-visible:ring-[#1E4FFF]"
              data-testid="reset-pw2-input"
            />
          </div>
          {err && (
            <div className="text-sm text-[#E13C32] bg-[#E13C32]/10 border border-[#E13C32]/30 p-3 rounded-sm" data-testid="reset-error">
              {err}
            </div>
          )}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#1E4FFF] hover:bg-[#1740D6] text-white font-label h-12 rounded-sm"
            data-testid="reset-submit-button"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset password →"}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}

function AuthShell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white text-[#0B0B12] p-8">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 mb-12" data-testid="auth-brand">
          <div className="w-8 h-8 bg-[#1E4FFF] flex items-center justify-center rounded-sm">
            <Trophy className="w-5 h-5 text-white" />
          </div>
          <span className="font-logo text-xl whitespace-nowrap text-[#0B0B12]">SALES<span className="text-[#1E4FFF]">ARENA HQ</span></span>
        </Link>
        {children}
      </div>
    </div>
  );
}
